import { gql } from '@apollo/client';

// Auth Mutations
export const REGISTER = gql`
  mutation Register($input: RegisterInput!) {
    register(input: $input) {
      success
      message
      user {
        id
        email
        username
      }
      requiresVerification
      verificationType
    }
  }
`;

export const LOGIN = gql`
  mutation Login($username: String!, $password: String!) {
    login(username: $username, password: $password) {
      success
      message
      user {
        id
        email
        username
        firstName
        lastName
        displayName
      }
      requiresVerification
      verificationType
    }
  }
`;

export const LOGOUT = gql`
  mutation Logout {
    logout {
      success
      message
    }
  }
`;

export const VERIFY_PHONE = gql`
  mutation VerifyPhone($email: String!, $code: String!) {
    verifyPhone(email: $email, code: $code) {
      success
      message
      user {
        id
        email
        phoneVerified
      }
      requiresVerification
      verificationType
    }
  }
`;

export const VERIFY_EMAIL = gql`
  mutation VerifyEmail($email: String!, $code: String!) {
    verifyEmail(email: $email, code: $code) {
      success
      message
      user {
        id
        email
        emailVerified
        isActive
      }
      requiresVerification
    }
  }
`;

export const RESEND_VERIFICATION = gql`
  mutation ResendVerification($email: String!, $verificationType: String!) {
    resendVerification(email: $email, verificationType: $verificationType) {
      success
      message
    }
  }
`;

export const REQUEST_PASSWORD_RESET = gql`
  mutation RequestPasswordReset($email: String!) {
    requestPasswordReset(email: $email) {
      success
      message
    }
  }
`;

export const RESET_PASSWORD = gql`
  mutation ResetPassword($token: String!, $newPassword: String!) {
    resetPassword(token: $token, newPassword: $newPassword) {
      success
      message
    }
  }
`;

export const UPDATE_PROFILE = gql`
  mutation UpdateProfile($input: UpdateProfileInput!) {
    updateProfile(input: $input) {
      success
      message
      user {
        id
        firstName
        lastName
        displayName
        bio
        address
        city
        state
        country
        dateOfBirth
        gender
        website
        secondaryPhone
      }
    }
  }
`;

export const UPDATE_PROFILE_PICTURE = gql`
  mutation UpdateProfilePicture($image: String!) {
    updateProfilePicture(image: $image) {
      success
      message
      user {
        id
        profilePicture
      }
    }
  }
`;

export const UPDATE_COVER_IMAGE = gql`
  mutation UpdateCoverImage($image: String!) {
    updateCoverImage(image: $image) {
      success
      message
      user {
        id
        coverImage
      }
    }
  }
`;

export const CHANGE_PASSWORD = gql`
  mutation ChangePassword($oldPassword: String!, $newPassword: String!) {
    changePassword(oldPassword: $oldPassword, newPassword: $newPassword) {
      success
      message
    }
  }
`;

// Ad Mutations
export const CREATE_AD = gql`
  mutation CreateAd($input: CreateAdInput!) {
    createAd(input: $input) {
      success
      message
      ad {
        id
        title
        slug
        status
      }
    }
  }
`;

export const UPDATE_AD = gql`
  mutation UpdateAd($input: UpdateAdInput!) {
    updateAd(input: $input) {
      success
      message
      ad {
        id
        title
        slug
        status
      }
    }
  }
`;

export const TOGGLE_AD_STATUS = gql`
  mutation ToggleAdStatus($id: ID!) {
    toggleAdStatus(id: $id) {
      success
      message
      ad {
        id
        status
        isActive
      }
    }
  }
`;

export const TOGGLE_FAVORITE = gql`
  mutation ToggleFavorite($adId: ID!) {
    toggleFavorite(adId: $adId) {
      success
      message
      isFavorited
    }
  }
`;

export const CONTACT_AD = gql`
  mutation ContactAd($input: ContactAdInput!) {
    contactAd(input: $input) {
      success
      message
    }
  }
`;

export const REQUEST_BOOST = gql`
  mutation RequestBoost($adId: ID!, $packageId: ID, $durationDays: Int) {
    requestBoost(adId: $adId, packageId: $packageId, durationDays: $durationDays) {
      success
      message
      boostRequest {
        id
        ad {
          id
          title
          slug
        }
        package {
          id
          name
          price
          durationDays
        }
        durationDays
        amount
        status
        requestedAt
      }
    }
  }
`;

export const UPLOAD_PAYMENT_PROOF = gql`
  mutation UploadPaymentProof($boostRequestId: ID!, $paymentProof: String!, $transactionId: String) {
    uploadPaymentProof(boostRequestId: $boostRequestId, paymentProof: $paymentProof, transactionId: $transactionId) {
      success
      message
      boostRequest {
        id
        status
        paymentProofUrl
        paymentUploadedAt
        transactionId
      }
    }
  }
`;

export const APPROVE_BOOST_REQUEST = gql`
  mutation ApproveBoostRequest($boostRequestId: ID!, $notes: String) {
    approveBoostRequest(boostRequestId: $boostRequestId, notes: $notes) {
      success
      message
      boostRequest {
        id
        status
        approvedBy {
          id
          username
          displayName
        }
        approvedAt
        boostStartedAt
        boostEndsAt
      }
    }
  }
`;

export const REJECT_BOOST_REQUEST = gql`
  mutation RejectBoostRequest($boostRequestId: ID!, $reason: String!) {
    rejectBoostRequest(boostRequestId: $boostRequestId, reason: $reason) {
      success
      message
      boostRequest {
        id
        status
        rejectionReason
        approvedBy {
          id
          username
          displayName
        }
        approvedAt
      }
    }
  }
`;

export const CREATE_BOOST_PACKAGE = gql`
  mutation CreateBoostPackage($name: String!, $description: String!, $packageType: String!, $durationDays: Int!, $price: Float!, $features: String, $isActive: Boolean) {
    createBoostPackage(name: $name, description: $description, packageType: $packageType, durationDays: $durationDays, price: $price, features: $features, isActive: $isActive) {
      success
      message
      package {
        id
        name
        description
        packageType
        durationDays
        price
        features
        isActive
      }
    }
  }
`;

// Report Mutations
export const REPORT_AD = gql`
  mutation ReportAd($input: ReportAdInput!) {
    reportAd(input: $input) {
      success
      message
      report {
        id
        reason
        status
      }
    }
  }
`;

export const REPORT_USER = gql`
  mutation ReportUser($input: ReportUserInput!) {
    reportUser(input: $input) {
      success
      message
      report {
        id
        reason
        status
      }
    }
  }
`;

// Admin Mutations
export const SET_SELLER_VERIFIED = gql`
  mutation SetSellerVerified($username: String!, $verified: Boolean!) {
    setSellerVerified(username: $username, verified: $verified) {
      success
      message
    }
  }
`;

// Notification Mutations
export const MARK_NOTIFICATION_READ = gql`
  mutation MarkNotificationRead($notificationId: ID!) {
    markNotificationRead(notificationId: $notificationId) {
      success
      message
    }
  }
`;

export const MARK_ALL_NOTIFICATIONS_READ = gql`
  mutation MarkAllNotificationsRead {
    markAllNotificationsRead {
      success
      count
    }
  }
`;

// Rating Mutations
export const RATE_USER = gql`
  mutation RateUser($input: RateUserInput!) {
    rateUser(input: $input) {
      success
      message
      rating {
        id
        score
        review
        createdAt
      }
    }
  }
`;

// Messaging Mutations
export const START_CONVERSATION = gql`
  mutation StartConversation($recipientUsername: String!, $message: String!, $adId: ID) {
    startConversation(recipientUsername: $recipientUsername, message: $message, adId: $adId) {
      success
      error
      conversation {
        id
        updatedAt
      }
    }
  }
`;

export const SEND_MESSAGE = gql`
  mutation SendMessage($conversationId: ID!, $body: String!) {
    sendMessage(conversationId: $conversationId, body: $body) {
      success
      error
      message {
        id
        body
        isRead
        createdAt
        sender {
          id
          username
          displayName
          profilePicture
        }
      }
    }
  }
`;

export const MARK_THREAD_READ = gql`
  mutation MarkThreadRead($conversationId: ID!) {
    markThreadRead(conversationId: $conversationId) {
      success
      count
    }
  }
`;

export const RESPOND_TO_RATING = gql`
  mutation RespondToRating($input: RespondToRatingInput!) {
    respondToRating(input: $input) {
      success
      message
      response {
        id
        message
        createdAt
      }
    }
  }
`;
